import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FormattedMessage } from '@techstyle/react-intl';

import { mobile } from '../../styles';
import generateColorSwatchUrl from '../../utils/generateColorSwatchUrl';
import ProductSizeLabel from '../ProductSizeLabel/ProductSizeLabel';

const SetItemSummary = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
`;

const SetItemSelectedColorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 12px;
`;

const SetItemSelectedColorTitle = styled.div`
  font-size: 12px;
  letter-spacing: 1.75px;
  text-align: center;
  text-transform: uppercase;
  margin-bottom: ${props => props.theme.spacing.tiny}px;
`;

const SetItemSelectedSizeTitle = styled(SetItemSelectedColorTitle)``;

const SetItemSelectedColor = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: solid 2px ${props => props.theme.colors.default};
  background-color: ${props =>
    /^rgb/.test(props.value) || /^#/.test(props.value)
      ? props.value
      : props.theme.colors.gray150};
  background-image: ${props =>
    /^rgb/.test(props.value) || /^#/.test(props.value)
      ? 'none'
      : `url(${props.value})`};
  background-clip: content-box;
  background-size: cover;
  padding: 2px;

  ${mobile`
    width: 50px;
    height: 50px;
  `};
`;

const SetItemSelectedColorLabel = styled.div`
  font-size: 12px;
  font-weight: 300;
  letter-spacing: 0.3px;
  text-align: center;
  max-width: 80%;
`;

const SetItemSelectedSizeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 21px;
`;

const SetItemSelectedSize = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;

  border: solid 1px ${props => props.theme.colors.stone};

  font-size: 10px;
  text-align: center;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${mobile`
    width: 50px;
    height: 50px;
  `};
`;

function getProductSizeLabel(
  currentSelection,
  isOneSize,
  shouldDisplayMultipleOneSizes
) {
  // In some cases the size is not selected yet, and we don't have a default size
  // This should catch cases where we have multiple oneSize linked products in the set
  if (!currentSelection?.size?.sizeProduct) {
    return (
      <FormattedMessage
        id="site_pdp.select_size"
        defaultMessage="Select Size"
      />
    );
  }

  if (isOneSize) {
    return (
      <ProductSizeLabel
        isOneSize
        shouldDisplayMultipleOneSizes={shouldDisplayMultipleOneSizes}
        product={currentSelection.size.sizeProduct}
      />
    );
  }

  return currentSelection.size.sizeProduct.label_instance;
}

function ProductSetItemSummary({ currentSelection = {}, isOneSize = false }) {
  const skuWithoutSize = currentSelection.color.itemNumber.slice(0, -3);
  const colorUrl = generateColorSwatchUrl({ skuWithoutSize });

  const shouldDisplayMultipleOneSizes =
    currentSelection.color.sizes && currentSelection.color.sizes.length > 1;

  const sizeLabel = getProductSizeLabel(
    currentSelection,
    isOneSize,
    shouldDisplayMultipleOneSizes
  );

  return (
    <SetItemSummary data-testid="set-item-label">
      <SetItemSelectedColorWrapper>
        <SetItemSelectedColorTitle data-testid="selected-color">
          <FormattedMessage
            defaultMessage="Color"
            id="site_pdp.color_swatch_label"
          />
        </SetItemSelectedColorTitle>
        <SetItemSelectedColor value={colorUrl} />
        <SetItemSelectedColorLabel>
          {currentSelection.color.name}
        </SetItemSelectedColorLabel>
      </SetItemSelectedColorWrapper>
      <SetItemSelectedSizeWrapper>
        <SetItemSelectedSizeTitle>
          <FormattedMessage defaultMessage="Size" id="site_pdp.size_label" />
        </SetItemSelectedSizeTitle>
        <SetItemSelectedSize>{sizeLabel}</SetItemSelectedSize>
      </SetItemSelectedSizeWrapper>
    </SetItemSummary>
  );
}

ProductSetItemSummary.propTypes = {
  currentSelection: PropTypes.object,
  isOneSize: PropTypes.bool,
};

export default ProductSetItemSummary;
